import React, { useState, useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { SiteContent } from '../content';

const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 50) {
        el.classList.add('is-visible');
      } else {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };

  const { hero, invitation, form, closing } = SiteContent.contact;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper">
      {/* SECTION 1: HERO */}
      <section className="pt-40 md:pt-60 pb-12 text-center px-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll animate-fade-up">
            <h1 className="section-title text-5xl md:text-8xl mb-8 leading-none">{hero.title}</h1>
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <Quote large text={hero.quote} />
          </div>
        </div>
      </section>

      {/* SECTION 2 & 3: INVITATION & FORM */}
      <section className="pb-32 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-rich-black p-8 md:p-16 rounded-2xl shadow-2xl relative border-t-8 border-[#A65D3F] animate-on-scroll animate-scale-in">
            <p className="text-lg md:text-xl text-[#F5F0E6] mb-12 italic border-l-2 border-[#A65D3F]/60 pl-6">
              {invitation}
            </p>

            {submitted ? (
              <div className="text-center py-20 animate-fade-in">
                <div className="w-16 h-16 bg-[#A65D3F] rounded-full flex items-center justify-center mx-auto mb-8">
                   <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="section-title text-2xl text-white mb-4">{form.success.title}</h3>
                <p className="text-white/70 tracking-widest text-[10px] uppercase font-black">{form.success.desc}</p>
                <div className="pt-12">
                  <button onClick={() => setSubmitted(false)} className="text-[10px] font-black tracking-widest uppercase border-b border-[#A65D3F] text-[#A65D3F] pb-1">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="flex flex-col">
                    <label className="text-[10px] font-black tracking-widest uppercase text-white/50 mb-3">Name</label>
                    <input required type="text" placeholder="NAME" className="bg-white/5 border-b border-white/20 p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest uppercase" />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[10px] font-black tracking-widest uppercase text-white/50 mb-3">Email</label>
                    <input required type="email" placeholder="EMAIL" className="bg-white/5 border-b border-white/20 p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest uppercase" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] font-black tracking-widest uppercase text-white/50 mb-3">I'm interested in...</label>
                  <select className="w-full bg-white/5 border-b border-white/20 p-4 text-white outline-none cursor-pointer font-condensed tracking-widest uppercase appearance-none">
                    <option value="art">Purchasing Art</option>
                    <option value="event">Attending an Event</option>
                    <option value="cert">Certification</option>
                    <option value="collab">Collaboration</option>
                    <option value="connect">Just Connecting</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] font-black tracking-widest uppercase text-white/50 mb-3">Your Message</label>
                  <textarea required rows={4} placeholder="HOW SHALL WE REGENERATE?" className="bg-white/5 border-b border-white/20 p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest resize-none"></textarea>
                </div>
                <div className="pt-4">
                  <button type="submit" disabled={isSubmitting} className="w-full btn-primary py-6 text-lg">
                    {isSubmitting ? 'SUBMITTING...' : 'PLANT THIS MESSAGE'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 5: CLOSING (Dark Background) */}
      <section className="section-padding bg-rich-black text-center">
        <div className="max-w-4xl mx-auto px-6">
          <Quote large text={closing} className="drop-shadow-sm" />
          <div className="mt-20 opacity-20">
             <div className="w-px h-20 bg-white mx-auto"></div>
             <p className="text-white text-[10px] tracking-[0.5em] mt-4 uppercase">Beneath the surface</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;